import {API} from 'aws-amplify'
import type {dbItem} from '@/shared/utils/crud-helper/models'
import {apiCallWithAuthCheck} from '../auth/core/AuthErrorInterceptor'
import type {ShareAccess} from '../widgets/core/storage/types'

export type {ShareAccess}

/**
 * Client for the JobService shared-folders API (`JobService/docs/shared-folders.md` in
 * omicscloud-cdk). Same base URL and sign-in as the app's other `/v1` calls.
 *
 * While sharing is in its pilot the backend answers 404 to every sharing route for anyone not on its
 * allowlist, and `GET /v1/me/features` says `{sharing: false}`. The web shows nothing about sharing
 * until that flag is true (see `useSharingAccess`).
 */
const apiName: string = process.env.REACT_APP_API_NAME || 'JobService'

/** A shared folder, as `GET /v1/shares` describes it. */
export interface ShareView {
  shareId: string
  /** Display name; the folder's own name unless the owner chose another. */
  name: string
  /** The folder, relative to the owner's root. Always ends with `/`. */
  folderPath: string
  ownerIdentityId: string
  ownerName: string
  access: ShareAccess
  /** Members besides the owner. */
  memberCount: number
  createdAt: string
  /**
   * The owner is moving or renaming the folder (`POST /v1/shares/moves`): reads work, every write
   * answers 409 `share_moving` until the move ends. Absent from older backends.
   */
  moving?: boolean
}

export interface SharesResponse {
  sharedByMe: ShareView[]
  sharedWithMe: ShareView[]
}

export interface SharingFeatures {
  sharing: boolean
}

/** Errors are `{code, message}`; never "Not authorized" (that would read as an expired session). */
export interface ShareApiErrorBody {
  code?: string
  message?: string
}

/** The HTTP status of a failed sharing call, when the server answered. */
export function shareErrorStatus(error: unknown): number | undefined {
  const status = (error as {response?: {status?: unknown}} | undefined)?.response?.status
  return typeof status === 'number' ? status : undefined
}

/**
 * The share is gone for this caller: it never existed, sharing stopped, the caller was removed
 * (404), or their access no longer allows the request (403).
 */
export function isShareUnavailable(error: unknown): boolean {
  const status = shareErrorStatus(error)
  return status === 403 || status === 404
}

/** The server's own message for a failed call, if it sent one. */
export function shareErrorMessage(error: unknown): string | undefined {
  const data = (error as {response?: {data?: ShareApiErrorBody}} | undefined)?.response?.data
  return typeof data?.message === 'string' && data.message ? data.message : undefined
}

/**
 * `GET /v1/me/features`. Any failure (backend not deployed, network, 5xx, even 401) reads as
 * "no sharing": the feature stays invisible rather than half-working. Deliberately not wrapped in
 * apiCallWithAuthCheck: a background feature probe must never be what opens the session-expired
 * dialog (the app's own calls do that), and in E2E runs an unmocked probe would.
 */
export async function getSharingFeatures(): Promise<SharingFeatures> {
  try {
    const response = await API.get(apiName, 'v1/me/features', {})
    return {sharing: response?.sharing === true}
  } catch {
    return {sharing: false}
  }
}

/** `GET /v1/shares`: folders the caller shares, and folders shared with them. */
export async function listShares(): Promise<SharesResponse> {
  const response = await apiCallWithAuthCheck(() => API.get(apiName, 'v1/shares', {}))
  return {
    sharedByMe: Array.isArray(response?.sharedByMe) ? response.sharedByMe : [],
    sharedWithMe: Array.isArray(response?.sharedWithMe) ? response.sharedWithMe : [],
  }
}

interface ShareFilesPage {
  files?: {items?: dbItem[]; count?: number; lastKey?: string | null}
}

/**
 * `GET /v1/shares/{id}/files`: every indexed item under `path` (`''` for the shared folder itself,
 * otherwise a folder path ending in `/`), all pages. `file` is relative to the shared folder.
 */
export async function listShareFiles(shareId: string, path: string): Promise<dbItem[]> {
  return apiCallWithAuthCheck(async () => {
    const route = `v1/shares/${encodeURIComponent(shareId)}/files`
    let items: dbItem[] = []
    let lastKey: string | undefined
    do {
      const page: ShareFilesPage = await API.get(apiName, route, {
        queryStringParameters: lastKey ? {path, lastKey} : {path},
      })
      items = items.concat(page?.files?.items ?? [])
      lastKey = page?.files?.lastKey ?? undefined
    } while (lastKey)
    return items
  })
}

export interface ShareDownloadUrl {
  url: string
  /** Seconds the URL stays valid (15 minutes, E11). */
  expiresIn: number
}

/**
 * `POST /v1/shares/{id}/files/download-url`: a presigned GET for one file in the shared folder.
 * With `download` the URL makes the browser save the file instead of showing it.
 */
export async function requestShareDownloadUrl(
  shareId: string,
  path: string,
  download: boolean
): Promise<ShareDownloadUrl> {
  return apiCallWithAuthCheck(async () => {
    const response = await API.post(
      apiName,
      `v1/shares/${encodeURIComponent(shareId)}/files/download-url`,
      {body: download ? {path, download: true} : {path}}
    )
    return {url: response?.url ?? '', expiresIn: Number(response?.expiresIn) || 900}
  })
}
